import React, { useEffect, useState, useContext, useRef } from 'react';
import NavBar from '../../common/NavBar';
import PageHeader from '../../common/PageHeader';
import WhySection from '../renewals/landingPage/WhySection';
import { QuotesData, VinWarrantyExpiry } from '../../common/CommonTypes';
import Login, { LoginErrors } from '../../common/Login/Login';
import { QuotesApi } from '../../../api/QuotesApi';
import { SiteApi } from '../../../api/SiteApi';
import { useNavigate } from 'react-router-dom';
import {
  CaptchaMessages,
  PageAbsolutePath,
  VehicleIdType
} from '../../../consts/Common';
import { SiteContext } from '../../../context/SiteContext';
import LandingPageProductSection from './LandingPageProductSection';
import RegistrationInput from '../../common/RegistrationInput';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import LoadingContainer from '../../common/LoadingContainer/LoadingContainer';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { setCountryCode } from '../../common/HelperMethods';
import TextInput from '../../common/TextInput/TextInput';
import { VehicleApi } from '../../../api/VehicleApi';

type CustomerLandingPageProps = {
  id: string;
  dealerNumber: string;
};

interface NewQuoteErrorObject {
  newQuoteVRN: string;
  newQuoteVIN: string;
  newQuoteError: string;
}

interface ValidationResultObject {
  isValid: boolean;
  validationMessage: string;
}

enum Status {
  Idle,
  GetStarted,
  NewQuoteByVrn
}

const CustomerLandingPage = ({
  id,
  dealerNumber
}: CustomerLandingPageProps): JSX.Element => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [quotes, setQuotes] = useState<QuotesData | undefined>(undefined);
  const [isValid, setIsValid] = useState(false);
  const [isCaptchaHidden, setIsCaptchaHidden] = useState(true);
  const navigate = useNavigate();

  const { site } = useContext(SiteContext);
  const isCallCentreUser = !!site.user?.userId;
  const { includeQuotes, includeOpenSite } = site;
  const [loading, setLoading] = useState<boolean>(isCallCentreUser);

  const cms = site.content.landingPage;

  const { useVinInput, forceVinCheck, vinNotFoundText } = cms;

  const products = site.content.productPage.productTypeCustomisation;
  const [errors, setErrors] = useState<LoginErrors>({});
  const [newQuoteVRN, setNewQuoteVRN] = useState('');
  const [newQuoteVIN, setNewQuoteVIN] = useState('');
  const [status, setStatus] = useState<Status>(Status.Idle);
  const [newQuoteValidationErrors, setNewQuoteValidationErrors] =
    useState<NewQuoteErrorObject>({
      newQuoteVRN: '',
      newQuoteVIN: '',
      newQuoteError: ''
    });

  const { t } = useTranslation();

  useEffect(() => {
    if (isCallCentreUser) {
      preCheckCallCentreLogin();
    }
  }, []);

  const onClickLogin = (email: string, password: string) => {
    validateCredentials(email, password);
  };

  const validateCredentials = (email: string, password: string) => {
    const controller = new AbortController();

    const successCallback = (data: QuotesData) => {
      if (data.hasExpired && !data.rollingQuote) {
        const newError = {
          vrnError: '',
          passwordError: '',
          invalidCredentialsError: t('landing-page.customer-quote-expired')
        };
        setErrors(newError);
      } else {
        setDetails(data);
      }

      setLoading(false);
    };

    const failureCallback = () => {
      const newError = {
        vrnError: '',
        passwordError: '',
        invalidCredentialsError: t('landing-page.customer-invalid-credential')
      };
      setErrors(newError);

      setLoading(false);
    };

    setLoading(true);

    new QuotesApi().getQuotesByCredentials(
      email,
      password,
      site.countryCode,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const setDetails = (data: QuotesData) => {
    setQuotes(data);
    setIsValid(true);
  };

  useEffect(() => {
    if (isValid === true) {
      const path = setCountryCode(
        PageAbsolutePath.Customer.VehicleDetails,
        site.countryCode
      );
      navigate(path, {
        state: {
          quote: quotes
        }
      });
    }
  }, [isValid]);

  const preCheckCallCentreLogin = () => {
    const controller = new AbortController();

    const successCallback = (data: QuotesData) => {
      setDetails(data);
      setLoading(false);
    };

    const failureCallback = () => {
      const path = setCountryCode(PageAbsolutePath.Error, site.countryCode);
      navigate(path, {
        state: {
          error: 'Quote not found.'
        }
      });

      setIsValid(false);
      setLoading(false);
    };

    new QuotesApi().getQuotesDataFromId(
      id,
      dealerNumber,
      site.countryCode,
      site.user.token,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const validateVrn = (vrn: string): ValidationResultObject => {
    let validationMessage = '';
    const vrnCharLimit = 10;

    if (CommonValidation.isEmpty(vrn)) {
      validationMessage = t('landing-page.cj-error-empty-vrn');
    } else if (!CommonValidation.isValidLength(vrn, vrnCharLimit)) {
      validationMessage = t('landing-page.vrn-exceeds-length-error', {
        vrnCharLimit: vrnCharLimit
      });
    }

    if (validationMessage === '') {
      return {
        isValid: true,
        validationMessage
      };
    } else {
      return {
        isValid: false,
        validationMessage
      };
    }
  };

  const validateVin = (vin: string): ValidationResultObject => {
    let validationMessage = '';

    if (CommonValidation.isEmpty(vin)) {
      validationMessage = t('landing-page.vin-required-message');
    }

    return {
      isValid: validationMessage === '',
      validationMessage
    };
  };

  const onChangeVehicleReg = (e: React.FormEvent<HTMLInputElement>) => {
    setNewQuoteVRN(e.currentTarget.value);
  };

  const onChangeVehicleVin = (e: React.FormEvent<HTMLInputElement>) => {
    setNewQuoteVIN(e.currentTarget.value);
  };

  const handleEnterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key == 'Enter') {
      e.preventDefault();
    }
  };
  const getStarted = () => {
    const resultVrn = validateVrn(newQuoteVRN);
    const resultVin = validateVin(newQuoteVIN);
    if (resultVrn.isValid || (useVinInput && resultVin.isValid)) {
      setNewQuoteValidationErrors({
        newQuoteVRN: '',
        newQuoteVIN: '',
        newQuoteError: ''
      });

      if (site.user.captchaDisable) navigateToVehiclesPage();
      else setIsCaptchaHidden(false);
    } else {
      setNewQuoteValidationErrors({
        ...newQuoteValidationErrors,
        newQuoteVRN: resultVrn.validationMessage,
        newQuoteVIN: resultVin.validationMessage
      });
    }

    setStatus(Status.Idle);
  };

  const navigateToVehiclesPage = async () => {
    const path = setCountryCode(
      PageAbsolutePath.Customer.VehicleDetails,
      site.countryCode
    );

    if (useVinInput) {
      if (forceVinCheck) {
        const expiryDetails = await loadVinWarrantyExpiryAsync(newQuoteVIN);
        if (expiryDetails) {
          navigate(path, {
            state: {
              idValue: newQuoteVIN,
              expiryDetails: expiryDetails,
              vehicleIdType: VehicleIdType.Vin
            }
          });
        } else {
          setNewQuoteValidationErrors({
            ...newQuoteValidationErrors,
            newQuoteVIN: vinNotFoundText
          });
        }
      } else {
        navigate(path, {
          state: {
            idValue: newQuoteVIN,
            vehicleIdType: VehicleIdType.Vin
          }
        });
      }
    } else {
      navigate(path, {
        state: {
          idValue: newQuoteVRN,
          vehicleIdType: VehicleIdType.Vrn
        }
      });
    }
  };

  const loadVinWarrantyExpiryAsync = async (
    vin: string
  ): Promise<VinWarrantyExpiry> => {
    return new Promise((resolve, reject) => {
      const controller = new AbortController();

      const successCallback = (result: VinWarrantyExpiry) => {
        console.log(result);
        resolve(result);
      };

      const failureCallback = (error: string) => {
        console.log(error);
        reject(error);
      };

      new VehicleApi().getVinWarrantyExpiry(
        vin,
        site.user.token,
        successCallback,
        failureCallback,
        controller.signal
      );
    });
  };

  const recaptchaChangeHandler = (response: string | null) => {
    if (response) {
      const controller = new AbortController();

      const successCallback = (hasAccess: boolean) => {
        if (hasAccess) {
          navigateToVehiclesPage();
        } else {
          setNewQuoteValidationErrors({
            ...newQuoteValidationErrors,
            newQuoteVRN: CaptchaMessages.ValidationMessage
          });
          recaptchaRef.current?.reset();
          setIsCaptchaHidden(true);
        }
      };

      const failureCallback = () => {
        setNewQuoteValidationErrors({
          ...newQuoteValidationErrors,
          newQuoteVRN: CaptchaMessages.ValidationMessage
        });
        recaptchaRef.current?.reset();
        setIsCaptchaHidden(true);
      };

      new SiteApi().validateCaptcha(
        response,
        successCallback,
        failureCallback,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    }
  };

  const onExpire = () => {
    recaptchaRef.current?.reset();
  };

  const renderOpenSiteSearch = () => {
    if (useVinInput) {
      return (
        <div>
          <div className='control-label'>{t('landing-page.cj-vin-label')}</div>
          <TextInput
            placeholder={t('landing-page.vehicle-identification-number')}
            name='vin-input'
            className='input-text w250'
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              onChangeVehicleVin(e)
            }
            onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) =>
              handleEnterKeyPress(e)
            }
            value={newQuoteVIN}
          />
          <div className='error-style'>
            {newQuoteValidationErrors.newQuoteVIN}
          </div>
        </div>
      );
    } else {
      return (
        <div>
          <div className='control-label'>
            {t('landing-page.cj-vehicle-registration-label')}
          </div>
          <div className='control'>
            <RegistrationInput
              label={t('landing-page.vehicle-registration')}
              name='newQuoteVRN'
              className='input-text'
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                onChangeVehicleReg(e)
              }
              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) =>
                handleEnterKeyPress(e)
              }
              value={newQuoteVRN}
              error={newQuoteValidationErrors.newQuoteVRN}
              hasError={newQuoteValidationErrors.newQuoteVRN !== ''}
            />
          </div>
        </div>
      );
    }
  };

  return (
    <div className='flex-column'>
      <LoadingContainer
        loading={loading}
        message={t('loading-placeholder.loading-text')}>
        <NavBar />
        <div className='flex-row mobile-column landing-page--top-section display-block'>
          <div className='flex-column landing-page--intro-wrapper'>
            <div className='landing-page--intro m2-bottom'>
              <PageHeader
                title={cms.title}
                isRenewalLandingPage={true}
                description={cms.description}
                descriptionClass='p1 extra-line-height lp-header-detail'
              />
              <div className='form'>
                <div className='flex-row'>
                  {includeOpenSite && (
                    <div className='flex-column'>
                      <div
                        className={
                          includeQuotes === true
                            ? 'card product-card290 product-card-height290'
                            : ''
                        }>
                        {renderOpenSiteSearch()}
                        <div
                          className='form-nav'
                          style={{ margin: '0.5rem 0rem' }}>
                          <>
                            <div>
                              <ButtonContinue
                                name='btnGetStarted'
                                text={t('customer-button.button-get-started')}
                                onClick={getStarted}
                                disabled={status !== Status.Idle}
                              />
                            </div>
                            <div className='error-style'>
                              {newQuoteValidationErrors.newQuoteError}
                            </div>
                          </>
                        </div>
                        <div
                          style={{
                            transform: 'scale(0.77)',
                            WebkitTransform: 'scale(0.77)',
                            transformOrigin: '0 0',
                            WebkitTransformOrigin: '0 0'
                          }}>
                          <small style={{ fontSize: '1rem' }}>
                            {t('landing-page.captcha.disclaimer-line-1')}
                            <a href='https://policies.google.com/privacy'>
                              {' '}
                              {t(
                                'landing-page.captcha.disclaimer-privacy-policy'
                              )}
                            </a>{' '}
                            {t('landing-page.captcha.disclaimer-line-2')}
                            <a href='https://policies.google.com/terms'>
                              {' '}
                              {t(
                                'landing-page.captcha.disclaimer-terms-of-service'
                              )}
                            </a>{' '}
                            {t('landing-page.captcha.disclaimer-line-3')}
                            <a
                              href='http://www.view-privacy-policy.co.uk/Privacy-Policy'
                              target='_blank'
                              rel='noopener noreferrer'>
                              {' '}
                              {t(
                                'landing-page.captcha.disclaimer-privacy-policy'
                              )}
                            </a>
                          </small>
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={site.user.recaptchaSiteKey}
                            onChange={recaptchaChangeHandler}
                            onExpired={onExpire}
                            hidden={isCaptchaHidden}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {includeQuotes && (
                    <div className='card product-card290 product-card-height290'>
                      <div className='product-card-header'>
                        <Login
                          showEmail={true}
                          emailMaxLength={50}
                          loginErrors={errors}
                          loading={loading}
                          onClickLogin={onClickLogin}
                          useVinInput={useVinInput}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className='flex-column'>
            <img
              className='landing-image'
              src={cms.backgroundImageUrl}
              alt='landing page image'
            />
          </div>
        </div>
        <WhySection cms={cms.whySection} showWhyTabs={false} />
        <LandingPageProductSection products={products} />
      </LoadingContainer>
    </div>
  );
};

export default CustomerLandingPage;
